import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type InactivityConfig = {
    maxIdleMs: number
    warnMs: number
}

export type InactivityPublic = {
    msToAutoClear: number
    warningActive: boolean
    cleared: boolean
    markActivity: () => void
    resetCleared: () => void
}

export function useInactivityTimers(
    cfg: InactivityConfig,
    onWarn: () => void,
    onAutoClear: () => Promise<void> | void
): InactivityPublic {
    const getNow = () => Date.now()

    const [lastActivityAt, setLastActivityAt] = useState<number>(getNow())
    const [warningActive, setWarningActive] = useState(false)
    const [cleared, setCleared] = useState(false)
    const [now, setNow] = useState<number>(getNow())

    const warnAt = useMemo(
        () => lastActivityAt + Math.max(0, cfg.maxIdleMs - cfg.warnMs),
        [lastActivityAt, cfg.maxIdleMs, cfg.warnMs]
    )
    const clearAt = useMemo(
        () => lastActivityAt + cfg.maxIdleMs,
        [lastActivityAt, cfg.maxIdleMs]
    )

    const warnT = useRef<number | null>(null)
    const clearT = useRef<number | null>(null)

    const clearTimers = () => {
        if (warnT.current) {
            clearTimeout(warnT.current)
            warnT.current = null
        }
        if (clearT.current) {
            clearTimeout(clearT.current)
            clearT.current = null
        }
    }

    const schedule = useCallback(() => {
        clearTimers()
        const now = getNow()
        const warnDelay = Math.max(0, warnAt - now)
        const clearDelay = Math.max(0, clearAt - now)

        warnT.current = window.setTimeout(() => {
            setWarningActive(true)
            onWarn?.()
        }, warnDelay)

        clearT.current = window.setTimeout(async () => {
            setWarningActive(false)
            setCleared(true)
            await onAutoClear?.()
        }, clearDelay)
    }, [warnAt, clearAt, onWarn, onAutoClear])

    useEffect(() => {
        schedule()
        return clearTimers
    }, [schedule])

    useEffect(() => {
        const id = window.setInterval(() => setNow(getNow()), 1000)
        return () => clearInterval(id)
    }, [])

    const msToAutoClear = Math.max(0, clearAt - now)

    const markActivity = useCallback(() => {
        setCleared(false)
        setWarningActive(false)
        setLastActivityAt(getNow())
    }, [])

    const resetCleared = useCallback(() => {
        setCleared(false)
        setWarningActive(false)
        setLastActivityAt(getNow())
    }, [])

    return { msToAutoClear, warningActive, cleared, markActivity, resetCleared }
}

export function fmtCountdown(ms: number) {
    const s = Math.max(0, Math.floor(ms / 1000))
    const m = Math.floor(s / 60)
    const ss = String(s % 60).padStart(2, '0')
    return m > 0 ? `${m}:${ss}` : `${ss}s`
}
